import { useState } from 'react';
import { Button, Switch } from '@/components/ui';
import { patchClientConnections, seedDemoContent, seedDemoData, summarizeDemoLeads } from '@/data/client';
import type { ClientFormProps } from './types';
import { CONNECTION_TABS, connectionsFromClient, pickConnections } from './data';
import { useDraft } from './useDraft';
import { SaveFooter } from './SaveFooter';
import { apiErrMsg } from './util';

const KEYS = CONNECTION_TABS.demo;

/* The demo flag and its reseed button. Under Advanced because flipping it
   swaps every report on the client's dashboard for synthetic data. */
export function DemoForm({ client, onSaved, onDirtyChange }: ClientFormProps) {
  const f = useDraft({
    formId: 'demo',
    initial: connectionsFromClient(client),
    keys: KEYS,
    persist: (d) => patchClientConnections(client.orgId, pickConnections(d, KEYS)),
    onSaved, onDirtyChange,
  });
  const { draft: conn, set: setC } = f;
  const [reseeding, setReseeding] = useState(false);
  const [reseedMsg, setReseedMsg] = useState('');

  /* Summaries come from the real lead grader, a few leads per request, so the
     browser loops until none are left. They persist across reseeds. */
  const summarize = async (prefix = '') => {
    let total = 0;
    for (let round = 0; round < 60; round++) {
      const r = await summarizeDemoLeads(client.orgId);
      if (r.skipped) { setReseedMsg(`${prefix}Summaries skipped: ${r.skipped}`); return; }
      total += r.done;
      setReseedMsg(`${prefix}AI summaries: ${total} written, ${r.remaining} to go…`);
      if (r.remaining === 0 || r.done === 0) break;
    }
    setReseedMsg(`${prefix}AI summaries complete (${total} written).`);
  };

  return (
    <div className="admin-form">
      <div className="admin-form__body">
        <section className="admin-sect">
          <h4 className="admin-sect__title">Demo account</h4>
          <div className="admin-prof">
            <Switch
              label="Demo account (synthetic data)"
              checked={conn.isDemo}
              onChange={(e) => setC('isDemo', e.target.checked)}
            />
            {conn.isDemo && (
              <p className="admin-hint" style={{ gridColumn: '1 / -1', margin: 0 }}>
                Skips real syncs and serves generated demo data (leads, traffic, campaigns, reviews) kept current daily. contentIQ is seeded once from fixtures with the button below. For prospect demos.
              </p>
            )}
            {client.isDemo && (
              <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 10 }}>
                <Button variant="outline" size="sm" disabled={reseeding} onClick={async () => {
                  setReseeding(true); setReseedMsg('');
                  try {
                    const r = await seedDemoData(client.orgId);
                    setReseedMsg(`Seeded ${r.leads} demo leads. `);
                    await summarize(`Seeded ${r.leads} demo leads. `);
                  }
                  catch (e) { setReseedMsg(apiErrMsg(e, 'Reseed failed.')); }
                  finally { setReseeding(false); }
                }}>{reseeding ? 'Working…' : 'Reseed demo leads'}</Button>
                <Button variant="ghost" size="sm" disabled={reseeding} onClick={async () => {
                  setReseeding(true); setReseedMsg('');
                  try { await summarize(); }
                  catch (e) { setReseedMsg(apiErrMsg(e, 'Summaries failed.')); }
                  finally { setReseeding(false); }
                }}>Generate missing AI summaries</Button>
                <Button variant="ghost" size="sm" disabled={reseeding} onClick={async () => {
                  setReseeding(true); setReseedMsg('');
                  try {
                    const r = await seedDemoContent(client.orgId);
                    setReseedMsg(`contentIQ seeded: ${r.sources} vault sources, ${r.articles} articles, ${r.topics} topics, ${r.blogTopics} blog ideas, ${r.socialPosts} social posts.`);
                  }
                  catch (e) { setReseedMsg(apiErrMsg(e, 'Content seed failed.')); }
                  finally { setReseeding(false); }
                }}>Seed contentIQ demo</Button>
                {reseedMsg && <span className="dash-muted" style={{ fontSize: 'var(--text-xs)' }}>{reseedMsg}</span>}
              </div>
            )}
          </div>
        </section>
      </div>
      <SaveFooter dirty={f.dirty} saving={f.saving} err={f.err} onSave={f.save} label="Save demo setting" sticky={false} />
    </div>
  );
}
